import {
    DistributionMethod, DriverOperationalAverageMetric,
    GetSinglePropertyDriversAndWorksheetItemsNoCalcEngineQuery,
    GetSinglePropertyDriversAndWorksheetItemsQuery,
    GrowthCalcMethod,
    MonthlyAdjustmentType,
    MonthlyAverageBasePeriod,
    PayrollDriverCompensationItemPositionsModel,
    PercentGrowthBasePeriod,
    RevenueType,
    VersionType,
} from "../../../../__generated__/generated_types";

export type TOpDriverNameType = {
    name: string,
    type: string,
}

export function hydrateDriverAssumptions (data: GetSinglePropertyDriversAndWorksheetItemsQuery): TDriverMetrics {
    const {
        operationalMetricDriver,
        operationalMonthlyAverage,
        revenueDriver,
        worksheetDriver,
        growthDriver,
        accountPercentageDriver,
        payrollDriver,
        renovationsDriver,
        customDriver,
        year,
        versionType,
    } = data.singlePropertyAccount;

    // determine if this account actually contains any drivers
    const isDriven = (operationalMetricDriver?.isDriven ?? false)
        || (revenueDriver?.isDriven ?? false)
        || (worksheetDriver?.isDriven ?? false)
        || (growthDriver && growthDriver.length > 0)
        || (accountPercentageDriver?.isDriven ?? false)
        || (payrollDriver?.isDriven ?? false)
        || (renovationsDriver?.isDriven ?? false)
        || (customDriver?.isDriven ?? false)
        || (operationalMonthlyAverage?.isDriven ?? false);

    const drivers = {
        operational: [],
        revenue: [],
        worksheet: [],
        monthlyAverage: [],
        yearlyAverage: [],
        percentGrowth: [],
        annualTargetValue: [],
        account: [],
        accountPercentageAugment: {
            minValue: null,
            maxValue: null
        },
        payroll: [],
        renovations: [],
        customDriver: [],
        operationalMonthlyAverage: null
    } as TDriverMetricsYear;

    // only look for drivers to build if the account is driven
    if (isDriven) {
        // hydrate operational drivers
        operationalMetricDriver?.sourceMetrics.forEach(metric => {
            drivers.operational.push({
                type: metric.metricName,
                secondaryType: metric.secondaryMetricName ?? undefined,
                amount: metric.assumptionsAndValues[0]?.amount,
                sourceMetricId: metric.sourceMetricId,
                secondaryMetricId: metric.secondaryMetricId ?? undefined,
                driverMetricTypeId: metric.driverMetricTypeId,
                locked: metric.locked == undefined || metric.locked == null ? false : metric.locked,
                valuesLocked: metric.valuesLocked == undefined || metric.valuesLocked == null ? false : metric.valuesLocked,
                configLocked: metric.configLocked == undefined || metric.configLocked == null ? false : metric.configLocked,
                lookbackPeriod: metric.assumptionsAndValues[0]?.lookbackPeriod,
                percentage: metric.assumptionsAndValues.map(x => x.percentage),
                values: metric.assumptionsAndValues.map(x => x.amount),
                name: metric.name,
                createdAt: new Date(metric.createdAt),
                cumSum: metric.cumSum ?? false,
                nonProrated: metric.nonProrated ?? false,
                minValue: metric.minValue ?? null,
                maxValue: metric.maxValue ?? null,
            });
        });

        // hydrate custom drivers
        customDriver?.items.forEach(item => {
            drivers.customDriver.push({
                id: item.id,
                itemName: item.itemName,
                amount: item.assumptions.map(x => x.amount),
                percentage: item.assumptions.map(x => x.percentOf),
                count: item.assumptions.map(x => x.count),
                locked: item.locked == undefined || item.locked == null ? false : item.locked,
            });
        });
        drivers.customDriver.sort(
            (a, b) =>
                a.itemName > b.itemName ? 1 : a.itemName < b.itemName ? -1 : 0
        );

        // hydrate account percentage drivers
        accountPercentageDriver?.sourceAccounts.forEach(account => {
            drivers.account.push({
                accountId: account.accountId,
                glNumber: account.glNumber,
                glName: account.glName,
                assumptionId: account.assumptionId ?? '',
                acctPercentageDriverAccountId: account.acctPercentageDriverAccountId,
                lookbackPeriod: account.assumptionsAndValues[0]?.lookbackPeriod,
                percentage: account.assumptionsAndValues.map(x => x.percentage),
                values: account.assumptionsAndValues.map(x => x.accountValue),
            });
        });
        drivers.account.sort(
            (a, b) =>
                a.glName > b.glName ? 1 : a.glName < b.glName ? -1 : 0
        );

        // hydrate the account percentage augments
        if(accountPercentageDriver?.augments) {
            drivers.accountPercentageAugment = {
                minValue: accountPercentageDriver.augments.minValue,
                maxValue: accountPercentageDriver.augments.maxValue
            };
        }

        // hydrate worksheet drivers
        worksheetDriver?.worksheetLines.forEach(line => {
            drivers.worksheet.push({
                description: line.description,
                values: line.values,
                locked: line.locked,
                valuesLocked: line.valuesLocked,
            });
        });

        drivers.worksheet.sort(
            (a, b) =>
                a.description > b.description ? 1 : a.description < b.description ? -1 : 0
        );

        const appliedGrowthDriver = growthDriver[0];
        if (appliedGrowthDriver) {
            const {__typename, ...appliedGrowthDriverWOTypeName} = appliedGrowthDriver;
            if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.MonthlyAverage) {
                drivers.monthlyAverage.push(appliedGrowthDriverWOTypeName);
            } else if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.YearlyAverage) {
                drivers.yearlyAverage.push(appliedGrowthDriverWOTypeName);
            } else if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.PercentGrowth) {
                drivers.percentGrowth.push(appliedGrowthDriverWOTypeName);
            } else if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.AnnualTargetValue) {
                drivers.annualTargetValue.push(appliedGrowthDriverWOTypeName);
            }
        }

        // hydrate remaining drivers
        // Note: this array destructuring was introduced because these sort operations began failing and crashing the
        // frontend if values are present in either the sourceRevenueMetrics, compensationItems, or costCategories arrays
        drivers.revenue = [...revenueDriver?.sourceRevenueMetrics ?? []];
        drivers.revenue.sort(
            (a, b) => a.revenueType > b.revenueType ? 1 : a.revenueType < b.revenueType ? -1 : 0
        );

        drivers.payroll = [...payrollDriver?.compensationItems ?? []];
        drivers.payroll.sort(
            (a, b) => a.itemType > b.itemType ? 1 : a.itemType < b.itemType ? -1 : 0
        );

        drivers.renovations = [...renovationsDriver?.costCategories ?? []];
        drivers.renovations.sort(
            (a, b) => a.name > b.name ? 1 : a.name < b.name ? -1 : 0
        );

        if (operationalMonthlyAverage) {
            drivers.operationalMonthlyAverage = {
                locked: operationalMonthlyAverage.locked,
                sourceMetric: operationalMonthlyAverage.assumptionsAndValues.operationalMetricType,
                startMonth: operationalMonthlyAverage.assumptionsAndValues.startMonth,
                endMonth: operationalMonthlyAverage.assumptionsAndValues.endMonth,
                monthlyGrowth: operationalMonthlyAverage.assumptionsAndValues.monthlyGrowth,
                yearlyGrowth: operationalMonthlyAverage.assumptionsAndValues.yearlyGrowth,
                customIncrease: operationalMonthlyAverage.assumptionsAndValues.customIncrease,
            };
        }
    }

    return {
        year,
        versionType,
        isDriven,
        drivers,
    };
}

export function hydrateDriverAssumptionsNoCalcEngine (data: GetSinglePropertyDriversAndWorksheetItemsNoCalcEngineQuery): TDriverMetrics {
    const {
        operationalMetricDriver,
        operationalMonthlyAverage,
        revenueDriver,
        worksheetDriver,
        accountPercentageDriver,
        growthDriver,
        payrollDriver,
        renovationsDriver,
        customDriver,
        year,
        versionType,
    } = data.singlePropertyAccount;

    // determine if this account actually contains any drivers
    const isDriven = (operationalMetricDriver?.isDriven ?? false)
        || (revenueDriver?.isDriven ?? false)
        || (worksheetDriver?.isDriven ?? false)
        || (growthDriver && growthDriver.length > 0)
        || (accountPercentageDriver?.isDriven ?? false)
        || (payrollDriver?.isDriven ?? false)
        || (renovationsDriver?.isDriven ?? false)
        || (customDriver?.isDriven ?? false)
        || (operationalMonthlyAverage?.isDriven ?? false);

    const drivers = {
        operational: [],
        revenue: [],
        worksheet: [],
        account: [],
        accountPercentageAugment: {
            minValue: null,
            maxValue: null
        },
        payroll: [],
        monthlyAverage: [],
        yearlyAverage: [],
        percentGrowth: [],
        annualTargetValue: [],
        renovations: [],
        customDriver: [],
        operationalMonthlyAverage: null
    } as TDriverMetricsYear;

    // only look for drivers to build if the account is driven
    if (isDriven) {
        // hydrate operational drivers
        operationalMetricDriver?.sourceMetricAssumptions.forEach(metric => {
            drivers.operational.push({
                type: metric.metricName,
                secondaryType: metric.secondaryMetricName ?? undefined,
                amount: metric.assumptions[0]?.amount,
                sourceMetricId: metric.sourceMetricId,
                secondaryMetricId: metric.secondaryMetricId ?? undefined,
                driverMetricTypeId: metric.driverMetricTypeId,
                locked: metric.locked == undefined || metric.locked == null ? false : metric.locked,
                valuesLocked: metric.valuesLocked == undefined || metric.valuesLocked == null ? false : metric.valuesLocked,
                configLocked: metric.configLocked == undefined || metric.configLocked == null ? false : metric.configLocked,
                lookbackPeriod: metric.assumptions[0]?.lookbackPeriod,
                percentage: metric.assumptions.map(x => x.percentage),
                values: metric.assumptions.map(x => x.amount),
                name: metric.name,
                createdAt: new Date(metric.createdAt),
                cumSum: metric.cumSum ?? false,
                nonProrated: metric.nonProrated ?? false,
                minValue: metric.minValue ?? null,
                maxValue: metric.maxValue ?? null,
            });
        });

        // hydrate custom drivers
        customDriver?.items.forEach(item => {
            drivers.customDriver.push({
                id: item.id,
                itemName: item.itemName,
                amount: item.assumptions.map(x => x.amount),
                percentage: item.assumptions.map(x => x.percentOf),
                count: item.assumptions.map(x => x.count),
                locked: item.locked
            });
        });
        drivers.customDriver.sort(
            (a, b) =>
                a.itemName > b.itemName ? 1 : a.itemName < b.itemName ? -1 : 0
        );

        // hydrate account percentage drivers
        accountPercentageDriver?.sourceAccountAssumptions.forEach(account => {
            drivers.account.push({
                accountId: account.accountId,
                glNumber: account.glNumber,
                glName: account.glName,
                assumptionId: account.assumptionId ?? '',
                acctPercentageDriverAccountId: account.acctPercentageDriverAccountId,
                lookbackPeriod: account.assumptions[0]?.lookbackPeriod,
                percentage: account.assumptions.map(x => x.percentage),
                values: [],
            });
        });
        drivers.account.sort(
            (a, b) =>
                a.glName > b.glName ? 1 : a.glName < b.glName ? -1 : 0
        );

        // hydrate the account percentage augments
        if(accountPercentageDriver?.augments) {
            drivers.accountPercentageAugment = {
                minValue: accountPercentageDriver.augments.minValue,
                maxValue: accountPercentageDriver.augments.maxValue
            };
        }

        // hydrate worksheet drivers
        worksheetDriver?.worksheetLines.forEach(line => {
            drivers.worksheet.push({
                description: line.description,
                values: line.values,
                locked: line.locked,
                valuesLocked: line.valuesLocked,
            });
        });

        drivers.worksheet.sort(
            (a, b) =>
                a.description > b.description ? 1 : a.description < b.description ? -1 : 0
        );

        const appliedGrowthDriver = growthDriver[0];
        if (appliedGrowthDriver) {
            const {__typename, ...appliedGrowthDriverWOTypeName} = appliedGrowthDriver;
            if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.MonthlyAverage){
                drivers.monthlyAverage.push(appliedGrowthDriverWOTypeName);
            } else if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.YearlyAverage) {
                drivers.yearlyAverage.push(appliedGrowthDriverWOTypeName);
            } else if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.PercentGrowth) {
                drivers.percentGrowth.push(appliedGrowthDriverWOTypeName);
            } else if (appliedGrowthDriverWOTypeName.growthCalcMethod === GrowthCalcMethod.AnnualTargetValue) {
                drivers.annualTargetValue.push(appliedGrowthDriverWOTypeName);
            }
        }

        // hydrate remaining drivers
        // Note: this array destructuring was introduced because these sort operations began failing and crashing the
        // frontend if values are present in either the sourceRevenueMetrics, compensationItems, or costCategories arrays
        drivers.revenue = [...revenueDriver?.sourceRevenueMetrics ?? []];
        drivers.revenue.sort(
            (a, b) => a.revenueType > b.revenueType ? 1 : a.revenueType < b.revenueType ? -1 : 0
        );

        drivers.payroll = [...payrollDriver?.compensationItems ?? []];
        drivers.payroll.sort(
            (a, b) => a.itemType > b.itemType ? 1 : a.itemType < b.itemType ? -1 : 0
        );

        drivers.renovations = [...renovationsDriver?.costCategories ?? []];
        drivers.renovations.sort(
            (a, b) => a.name > b.name ? 1 : a.name < b.name ? -1 : 0
        );

        if (operationalMonthlyAverage) {
            drivers.operationalMonthlyAverage = {
                locked: operationalMonthlyAverage.locked,
                sourceMetric: operationalMonthlyAverage.assumptions.operationalMetricType,
                startMonth: operationalMonthlyAverage.assumptions.startMonth,
                endMonth: operationalMonthlyAverage.assumptions.endMonth,
                monthlyGrowth: operationalMonthlyAverage.assumptions.monthlyGrowth,
                yearlyGrowth: operationalMonthlyAverage.assumptions.yearlyGrowth,
                customIncrease: operationalMonthlyAverage.assumptions.customIncrease,
            };
        }
    }

    return {
        year,
        versionType,
        isDriven,
        drivers,
    };
}

export function getNewOpDriverName(existingDriverNameTypes: TOpDriverNameType[], typeForNewName: string, secondaryTypeForNewName: string | null): string {
    const existingDriverNames = existingDriverNameTypes.filter(x => x.type == typeForNewName).map(x => x.name);
    let iterator = 0;
    let newName = null;

    while (!newName) {
        let potentialNewName = `Fee Per ${typeForNewName}`;
        if (secondaryTypeForNewName) {
            potentialNewName = `${secondaryTypeForNewName} Per ${typeForNewName}`;
        }

        if (iterator > 0) {
            potentialNewName = `${potentialNewName} ${iterator}`;
        }

        if (!existingDriverNames.includes(potentialNewName)) {
            newName = potentialNewName;
        }

        iterator ++;
    }

    return newName;
}

export type TDriverMetrics = {
    year: number,
    versionType: VersionType,
    isDriven: boolean,
    drivers: TDriverMetricsYear,
}

export type TDriverMetricsYear = {
    operational: TDriverOperationalMetric[],
    revenue: TDriverRevenueMetric[],
    worksheet: TDriverWorksheetMetric[],
    monthlyAverage: TDriverGrowthMetric[],
    yearlyAverage: TDriverGrowthMetric[],
    percentGrowth: TDriverGrowthMetric[],
    annualTargetValue: TDriverGrowthMetric[],
    account: TDriverAccountMetric[],
    accountPercentageAugment: TDriverAccountAugments,
    payroll: TDriverPayrollMetric[],
    renovations: TDriverRenovationsMetric[],
    customDriver: TDriverCustomItem[],
    operationalMonthlyAverage: TDriverOperationalMonthlyAverageMetric | null
}

export type TPendingDriverMetricsYear = {
    operational: TPendingDriverOperationalMetric[],
    customItems: TPendingDriverCustomItem[],
    revenue: TDriverRevenueMetric[],
    worksheet: TPendingDriverWorksheetMetric[],
    monthlyAverage: TPendingDriverGrowthMetric[],
    yearlyAverage: TPendingDriverGrowthMetric[],
    percentGrowth: TPendingDriverGrowthMetric[],
    annualTargetValue: TPendingDriverGrowthMetric[],
    account: TPendingDriverAccountMetric[],
    accountPercentageAugment: TDriverAccountAugments | null,
    payroll: TPendingDriverPayrollMetric[],
    operationalMonthlyAverage: TDriverOperationalMonthlyAverageMetric | null
}

export type TPendingDriverAccountMetric = {
    accountId: string;
    lookbackPeriod: number;
}

export type TPendingDriverOperationalMetric = {
    type: string,
    secondaryType: string | undefined,
    sourceMetricId: string,
    secondaryMetricId: string | undefined,
    driverMetricTypeId: string,
    lookbackPeriod: number | undefined | null,
    name: string,
    cumSum?: boolean,
    nonProrated?: boolean,
    newName?: string | null,
    minValue?: string | null,
    maxValue?: string | null,
}

export type TPendingDriverCustomItem = string;

export type TPendingDriverPayrollMetric = {
    compId: string;
    itemType: string;
    positions: PayrollDriverCompensationItemPositionsModel[];
}

export type TPendingDriverWorksheetMetric = {
    description: string,
}

export type TPendingDriverGrowthMetric = {
    id: string,
    growthCalcMethod: GrowthCalcMethod,
    percentGrowthBasePeriod?: PercentGrowthBasePeriod | null | undefined,
    percentGrowthAdjustmentValue?: number | null | undefined,
    distributionMethod?: DistributionMethod | null | undefined,
    monthlyAverageBasePeriod?: MonthlyAverageBasePeriod | null,
    monthlyAdjustmentType?: MonthlyAdjustmentType | null,
    monthlyAdjustmentValue?: number | null,
    lookbackPeriodStart?: string | null,
    lookbackPeriodEnd?: string | null,
    annualTargetValueManualEntry?: number | null,
}

export type TDriverOperationalMetric = {
    type: string,
    secondaryType: string | undefined,
    amount: number | undefined,
    sourceMetricId: string,
    secondaryMetricId: string | undefined,
    driverMetricTypeId: string,
    lookbackPeriod: number | undefined | null,
    percentage: (number | null)[],
    values: (number | null | string)[],
    locked: boolean,
    valuesLocked: boolean,
    configLocked: boolean,
    name: string,
    newName?: string | null,
    createdAt?: Date,
    cumSum: boolean,
    nonProrated: boolean,
    minValue: string | null,
    maxValue: string | null,
}

export type TDriverOperationalMonthlyAverageMetric = {
    locked: boolean;
    sourceMetric: DriverOperationalAverageMetric;
    startMonth: string;
    endMonth: string;
    monthlyGrowth?: number | string | null;
    yearlyGrowth?: number | string | null;
    customIncrease?: boolean;
}

export type TDriverCustomItem = {
    id: string,
    itemName: string,
    amount: (number | null)[],
    percentage: (number | null)[],
    count: (number | null)[],
    locked: boolean,
}

export type TDriverRevenueMetric = {
    sourceId: string,
    revenueType: RevenueType,
    locked: boolean,
}

export type TDriverWorksheetMetric = {
    description: string,
    values: (number | null | string)[],
    locked: boolean,
    valuesLocked: boolean,
}

export type TDriverGrowthMetric = {
    id: string,
    growthCalcMethod: GrowthCalcMethod,
    percentGrowthBasePeriod?: PercentGrowthBasePeriod | null | undefined,
    percentGrowthAdjustmentValue?: number | null | undefined,
    distributionMethod?: DistributionMethod | null | undefined,
    monthlyAverageBasePeriod?: MonthlyAverageBasePeriod | null,
    monthlyAdjustmentType?: MonthlyAdjustmentType | null,
    monthlyAdjustmentValue?: number | null,
    lookbackPeriodStart?: string | null,
    lookbackPeriodEnd?: string | null,
    annualTargetValueManualEntry?: number | null,
    locked: boolean,
}

export type TDriverAccountMetric = {
    accountId: string,
    assumptionId: string | undefined,
    acctPercentageDriverAccountId: string,
    glNumber?: string|null,
    glName: string,
    percentage: number[],
    lookbackPeriod?: number | null | undefined,
    values: (number | null | string)[],
}

export type TDriverAccountAugments = {
    // The values are decimals and are numeric strings or null.
    minValue: string | null;
    maxValue: string | null;
}

export type TDriverPayrollMetric = {
    itemType: string;
    positions: PayrollDriverCompensationItemPositionsModel[];
}

export type TDriverRenovationsMetric = {
    id: string,
    name: string,
}

export function reconstructOperationalPayload(inputPayload: TDriverOperationalMetric): TPendingDriverOperationalMetric {
    return {
        type: inputPayload.type,
        secondaryType: inputPayload.secondaryType,
        sourceMetricId: inputPayload.sourceMetricId,
        secondaryMetricId: inputPayload.secondaryMetricId,
        driverMetricTypeId: inputPayload.driverMetricTypeId,
        lookbackPeriod: inputPayload.lookbackPeriod,
        name: inputPayload.name,
    };
}
